/**
 * Map ExtractedPolicy into form-shaped partial data plus per-field metadata.
 *
 * The form has 42+ fields across 6 steps; not all live on a dec page. This
 * mapping populates what the dec page provides and attaches metadata
 * (confidence, source quote, original extracted value) so the review UI
 * and downstream step components can render "from your policy" indicators
 * and flag fields that need explicit user confirmation.
 */

import type { FullFormData } from "@/schemas/formSchemas";
import { CARRIER_OPTIONS } from "@/schemas/formSchemas";
import type {
  ExtractedPolicy,
  FieldMetadata,
  ExtractedFieldMetadata,
  Confidence,
} from "@/types/extraction";

export type MappedFormData = Partial<{
  step1: Partial<FullFormData["step1"]>;
  step2: Partial<FullFormData["step2"]>;
  step3: Partial<FullFormData["step3"]>;
  step5: Partial<FullFormData["step5"]>;
  step6: Partial<FullFormData["step6"]>;
}>;

export interface MappingResult {
  formData: MappedFormData;
  metadata: FieldMetadata;
}

type ExtractedFieldShape<T> = {
  value: T | null;
  confidence: Confidence;
  sourceQuote: string | null;
  pass2Agreement?: boolean;
};

function buildMeta<T>(
  field: ExtractedFieldShape<T>,
  originalForAudit?: unknown,
): ExtractedFieldMetadata {
  return {
    source: "extracted",
    confidence: field.confidence,
    sourceQuote: field.sourceQuote,
    originalExtractedValue:
      originalForAudit !== undefined ? originalForAudit : field.value,
    acknowledged: field.confidence === "high" || field.confidence === "not_found",
  };
}

function isPresent<T>(field: ExtractedFieldShape<T>): field is ExtractedFieldShape<T> & { value: NonNullable<T> } {
  return field.value !== null && field.value !== undefined && field.confidence !== "not_found";
}

function normalizeCarrier(extracted: string): {
  matched: FullFormData["step1"]["carrierName"];
  original: string;
} {
  const lower = extracted.trim().toLowerCase();
  for (const option of CARRIER_OPTIONS) {
    if (option.toLowerCase() === lower) {
      return { matched: option, original: extracted };
    }
  }
  // Try partial match: "State Farm Mutual" -> "State Farm"
  for (const option of CARRIER_OPTIONS) {
    if (option === "Other") continue;
    const optLower = option.toLowerCase();
    if (lower.includes(optLower) || optLower.includes(lower)) {
      return { matched: option, original: extracted };
    }
  }
  return { matched: "Other", original: extracted };
}

function mapUseClassToVehicleUse(
  useClass: "commute" | "pleasure" | "business" | "rideshare",
): FullFormData["step3"]["primaryVehicleUse"] {
  return useClass;
}

function mapPaymentFrequency(
  freq: "monthly" | "six_month" | "annual",
): FullFormData["step6"]["currentPremiumFrequency"] {
  return freq;
}

export function mapExtractedPolicyToForm(policy: ExtractedPolicy): MappingResult {
  const formData: MappedFormData = {};
  const metadata: FieldMetadata = {};

  // --- Step 1 ---
  formData.step1 = {};
  if (isPresent(policy.policy.carrierName)) {
    const { matched, original } = normalizeCarrier(policy.policy.carrierName.value);
    formData.step1.carrierName = matched;
    if (matched === "Other") {
      formData.step1.carrierNameOther = original;
    }
    metadata["step1.carrierName"] = buildMeta(policy.policy.carrierName, original);
  }
  if (isPresent(policy.policy.policyNumber)) {
    formData.step1.policyNumber = policy.policy.policyNumber.value;
    metadata["step1.policyNumber"] = buildMeta(policy.policy.policyNumber);
  }

  // --- Step 2 (primary named insured) ---
  const primaryInsured =
    policy.namedInsureds.find((n) => n.isPrimary) ?? policy.namedInsureds[0];

  if (primaryInsured) {
    formData.step2 = {};
    if (isPresent(primaryInsured.fullName)) {
      formData.step2.fullLegalName = primaryInsured.fullName.value;
      metadata["step2.fullLegalName"] = buildMeta(primaryInsured.fullName);
    }
    if (isPresent(primaryInsured.dateOfBirth)) {
      formData.step2.dateOfBirth = primaryInsured.dateOfBirth.value;
      metadata["step2.dateOfBirth"] = buildMeta(primaryInsured.dateOfBirth);
    }
    if (isPresent(primaryInsured.address)) {
      const addr = primaryInsured.address.value;
      formData.step2.mailingAddressStreet = addr.street;
      formData.step2.mailingAddressUnit = addr.unit ?? "";
      formData.step2.mailingAddressCity = addr.city;
      formData.step2.mailingAddressState = addr.state;
      formData.step2.mailingAddressZip = addr.zip;
      const addrMeta = buildMeta(primaryInsured.address);
      metadata["step2.mailingAddressStreet"] = addrMeta;
      metadata["step2.mailingAddressUnit"] = addrMeta;
      metadata["step2.mailingAddressCity"] = addrMeta;
      metadata["step2.mailingAddressState"] = addrMeta;
      metadata["step2.mailingAddressZip"] = addrMeta;
    }
  }

  // --- Step 3 (vehicles) ---
  if (policy.vehicles.length > 0) {
    const [primaryVehicle, ...additionalVehicles] = policy.vehicles;
    formData.step3 = {};
    mapVehicleToFlat(primaryVehicle, formData.step3, metadata, "step3");

    if (additionalVehicles.length > 0) {
      formData.step3.additionalVehicles = additionalVehicles.map((v, i) => {
        const target: Partial<FullFormData["step3"]> = {};
        mapVehicleToFlat(
          v,
          target,
          metadata,
          `step3.additionalVehicles.${i}`,
        );
        return target as FullFormData["step3"]["additionalVehicles"] extends readonly (infer U)[] | undefined
          ? U
          : never;
      });
    }
  }

  // --- Step 5 (co-insured drivers from non-primary named insureds) ---
  const coInsureds = policy.namedInsureds.filter((n) => n !== primaryInsured);
  if (coInsureds.length > 0) {
    formData.step5 = {
      hasCoInsured: true,
      coInsuredCount: coInsureds.length,
      drivers: coInsureds.map((insured, i) => {
        const basePath = `step5.drivers.${i}`;
        const driver: Partial<FullFormData["step5"]> extends {
          drivers?: readonly (infer U)[] | undefined;
        }
          ? Partial<U>
          : never = {} as never;

        if (isPresent(insured.fullName)) {
          (driver as Record<string, unknown>).fullLegalName = insured.fullName.value;
          metadata[`${basePath}.fullLegalName`] = buildMeta(insured.fullName);
        }
        if (isPresent(insured.dateOfBirth)) {
          (driver as Record<string, unknown>).dateOfBirth = insured.dateOfBirth.value;
          metadata[`${basePath}.dateOfBirth`] = buildMeta(insured.dateOfBirth);
        }
        return driver as FullFormData["step5"] extends { drivers?: readonly (infer U)[] | undefined }
          ? U
          : never;
      }),
    };
  }

  // --- Step 6 (premium info) ---
  formData.step6 = {};
  if (isPresent(policy.premium.totalAmount)) {
    formData.step6.currentPremiumAmount = policy.premium.totalAmount.value;
    metadata["step6.currentPremiumAmount"] = buildMeta(policy.premium.totalAmount);
  }
  if (isPresent(policy.premium.paymentFrequency)) {
    formData.step6.currentPremiumFrequency = mapPaymentFrequency(
      policy.premium.paymentFrequency.value,
    );
    metadata["step6.currentPremiumFrequency"] = buildMeta(policy.premium.paymentFrequency);
  }
  if (isPresent(policy.premium.autopayEnrolled)) {
    formData.step6.hasAutopay = policy.premium.autopayEnrolled.value;
    metadata["step6.hasAutopay"] = buildMeta(policy.premium.autopayEnrolled);
  }
  if (isPresent(policy.premium.paperlessEnrolled)) {
    formData.step6.hasPaperless = policy.premium.paperlessEnrolled.value;
    metadata["step6.hasPaperless"] = buildMeta(policy.premium.paperlessEnrolled);
  }
  if (isPresent(policy.premium.paysInFull)) {
    formData.step6.paysInFull = policy.premium.paysInFull.value;
    metadata["step6.paysInFull"] = buildMeta(policy.premium.paysInFull);
  }

  return { formData, metadata };
}

function mapVehicleToFlat(
  vehicle: ExtractedPolicy["vehicles"][number],
  target: Partial<FullFormData["step3"]>,
  metadata: FieldMetadata,
  pathPrefix: string,
): void {
  if (isPresent(vehicle.year)) {
    target.vehicleYear = vehicle.year.value;
    metadata[`${pathPrefix}.vehicleYear`] = buildMeta(vehicle.year);
  }
  if (isPresent(vehicle.make)) {
    target.vehicleMake = vehicle.make.value;
    metadata[`${pathPrefix}.vehicleMake`] = buildMeta(vehicle.make);
  }
  if (isPresent(vehicle.model)) {
    target.vehicleModel = vehicle.model.value;
    metadata[`${pathPrefix}.vehicleModel`] = buildMeta(vehicle.model);
  }
  if (isPresent(vehicle.lienHolder)) {
    target.vehicleLienHolder = vehicle.lienHolder.value;
    metadata[`${pathPrefix}.vehicleLienHolder`] = buildMeta(vehicle.lienHolder);
    // If lien holder is present, the vehicle is still financed or leased.
    // Don't guess which; leave payoff status for the user.
  }
  if (isPresent(vehicle.annualMileage)) {
    target.annualMileage = vehicle.annualMileage.value;
    metadata[`${pathPrefix}.annualMileage`] = buildMeta(vehicle.annualMileage);
  }
  if (isPresent(vehicle.useClass)) {
    target.primaryVehicleUse = mapUseClassToVehicleUse(vehicle.useClass.value);
    metadata[`${pathPrefix}.primaryVehicleUse`] = buildMeta(vehicle.useClass);
  }
}

/**
 * Count fields that still need explicit user confirmation in the review UI.
 * Fields with confidence "medium" or "low" that the user has not yet
 * acknowledged count toward this number.
 */
export function countUnconfirmedFields(metadata: FieldMetadata): number {
  return Object.values(metadata).filter(
    (m) =>
      (m.confidence === "medium" || m.confidence === "low") &&
      !m.acknowledged,
  ).length;
}
